/**
 * ResultToolbar - Compact result context bar
 * 
 * Premium redesign: Single-line compact filter chips with result count
 * - Left: Result count (subtle)
 * - Center: Active filter chips (compact pills)
 * - Right: Clear all action
 * 
 * Design principles:
 * - Maximum height ~36-40px 
 * - Minimal vertical padding
 * - Inline horizontal layout
 * - Non-redundant information only
 */

import React, { memo, useMemo } from 'react';
import { X as CloseIcon } from 'lucide-react';
import { useFilterStore } from '@/lib/FilterStore';
import { queryKeys } from '@/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Cuisine, Skill } from '@/lib/types';

interface ResultToolbarProps {
  className?: string;
  onResetAll?: () => void;
  cuisinesOverride?: Cuisine[];
  resultCount?: number;
  isLoading?: boolean;
  /** Hide delivery mode chip since it's shown in header */
  hideDeliveryMode?: boolean;
  /** Hide location chip since it's shown in header */
  hideLocation?: boolean;
}

/**
 * ResultToolbar - Compact inline filter bar above results
 * Only shows non-redundant filter information
 */
export const ResultToolbar = memo(function ResultToolbar({
  className,
  onResetAll,
  cuisinesOverride,
  resultCount: _resultCount,
  isLoading = false,
  hideDeliveryMode = true,
  hideLocation = true,
}: ResultToolbarProps) {
  const filterStore = useFilterStore();
  
  // Load cuisines and skills for labels
  const { data: cuisines = [] } = useQuery<Cuisine[]>({
    queryKey: queryKeys.cuisines.list(),
    queryFn: async () => {
      const response = await fetch('/api/cuisines', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch cuisines');
      return response.json();
    },
    staleTime: 30 * 60 * 1000,
  });
  
  const { data: skills = [] } = useQuery<Skill[]>({
    queryKey: queryKeys.skills.list(),
    queryFn: async () => {
      const response = await fetch('/api/skills', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch skills');
      return response.json();
    },
    staleTime: 30 * 60 * 1000,
  });
  
  const effectiveCuisines = cuisinesOverride?.length ? cuisinesOverride : cuisines;

  // Build compact filter chips - exclude redundant info
  const chips = useMemo(() => {
    const result: Array<{ key: string; label: string; onRemove: () => void }> = [];
    
    // Cuisines - compact display
    filterStore.cuisineIds.forEach(id => {
      const cuisine = effectiveCuisines.find(c => c.id === id);
      if (cuisine) {
        result.push({
          key: `cuisine-${id}`,
          label: cuisine.name,
          onRemove: () => {
            const newCuisines = filterStore.cuisineIds.filter(cid => cid !== id);
            filterStore.setCuisines(newCuisines);
          }
        });
      }
    });
    
    // Skills (Dietary)
    filterStore.skills.forEach(id => {
      const skill = skills.find(s => s.id === id);
      if (skill) {
        result.push({
          key: `skill-${id}`,
          label: skill.name,
          onRemove: () => {
            const newSkills = filterStore.skills.filter(sid => sid !== id);
            filterStore.setSkills(newSkills);
          }
        });
      }
    });
    
    // Open Now
    if (filterStore.openNow) {
      result.push({
        key: 'open-now',
        label: 'Geöffnet',
        onRemove: () => filterStore.setOpenNow(false)
      });
    }
    
    // Delivery Only (legacy) - only if not redundant
    if (filterStore.deliveryOnly && filterStore.deliveryMode !== 'delivery') {
      result.push({
        key: 'delivery-legacy',
        label: 'Lieferung',
        onRemove: () => filterStore.setDeliveryOnly(false)
      });
    }
    
    // Delivery Mode - skip if hideDeliveryMode is true (shown in header)
    if (!hideDeliveryMode) {
      if (filterStore.deliveryMode === 'pickup') {
        result.push({
          key: 'delivery-mode',
          label: 'Abholung',
          onRemove: () => filterStore.setDeliveryMode('delivery')
        });
      }
    }
    
    // Distance (only if not default)
    if (filterStore.distance !== 20) {
      result.push({
        key: 'distance',
        label: `${filterStore.distance} km`,
        onRemove: () => filterStore.setDistance(20)
      });
    }
    
    // Search Query
    const searchQuery = (filterStore.query || filterStore.searchQuery || '').trim();
    if (searchQuery) {
      result.push({
        key: 'search',
        label: `"${searchQuery.slice(0, 15)}${searchQuery.length > 15 ? '…' : ''}"`,
        onRemove: () => filterStore.setQuery('')
      });
    }
    
    // Location - skip if hideLocation is true (shown in header)
    if (!hideLocation && filterStore.location?.label) {
      result.push({
        key: 'location',
        label: filterStore.location.label.slice(0, 20),
        onRemove: () => filterStore.setLocation(null)
      });
    }
    
    return result;
  }, [filterStore, effectiveCuisines, skills, hideDeliveryMode, hideLocation]);
  
  const hasChips = chips.length > 0;
  
  // If no filters and no loading state, render nothing
  if (!hasChips && !isLoading) return null;
  
  const handleResetAll = () => {
    filterStore.resetAll();
    onResetAll?.();
  };
  
  return (
    <div 
      className={cn(
        "flex items-center gap-2",
        className
      )}
    >
      {/* Active filter chips - unified style */}
      {hasChips && (
        <>
          {/* Chips container */}
          <div 
            className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto scrollbar-hide" 
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {chips.map(chip => (
              <button
                key={chip.key}
                onClick={chip.onRemove}
                title={`Entfernen: ${chip.label}`}
                className={cn(
                  "inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-lg",
                  "bg-[#009544]/10 text-[#009544] border border-[#009544]/20",
                  "hover:bg-[#009544]/15 hover:border-[#009544]/30",
                  "transition-colors",
                  "flex-shrink-0 whitespace-nowrap",
                  "text-xs font-medium"
                )}
              >
                <span className="max-w-[80px] truncate">{chip.label}</span>
                <span className="w-4 h-4 flex items-center justify-center rounded hover:bg-[#009544]/20">
                  <CloseIcon className="w-3 h-3" />
                </span>
              </button>
            ))}
          </div>
          
          {/* Clear all - Icon only */}
          <button
            onClick={handleResetAll}
            title="Alle Filter löschen"
            className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
            aria-label="Alle Filter löschen"
          >
            <CloseIcon className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>
  );
});
